// TradeActivityCard - Displays trade activity messages as cards in conversations
// Feature: Trade activity messages in DMs

import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  getTradeSummary,
  type TradeAction,
  type TradeSummary,
  type TradeSummaryNFT,
} from '../../api/messages';
import MultiCollectionWarning from '../trades/MultiCollectionWarning';
import { is3DModelUrl } from '../../utils/media';

// Trade action configuration
const TRADE_ACTION_CONFIG: Partial<Record<
  TradeAction,
  { label: string; icon: string; isCelebratory?: boolean; isTerminal?: boolean; isSystemEvent?: boolean }
>> = {
  trade_proposed: { label: 'proposed a trade', icon: '📤' },
  trade_countered: { label: 'countered the trade', icon: '↩️' },
  trade_accepted: { label: 'accepted the trade', icon: '✅' },
  trade_declined: { label: 'declined the trade', icon: '❌', isTerminal: true },
  trade_cancelled: { label: 'cancelled the trade', icon: '🚫', isTerminal: true },
  trade_completed: { label: 'Trade completed!', icon: '🎉', isCelebratory: true, isTerminal: true },
  trade_invalidated: { label: 'Trade was invalidated', icon: '⚠️', isTerminal: true, isSystemEvent: true },
  // trade_signing_ready removed - old messages will fall through to default
};

/**
 * Determine which trades tab to link to based on action
 * - Terminal actions (completed, declined, cancelled) → history
 * - Everything else → pending (single combined tab for active trades)
 */
function getTradesTab(tradeAction: TradeAction): string {
  const config = TRADE_ACTION_CONFIG[tradeAction];

  if (config?.isTerminal) {
    return 'history';
  }

  return 'pending';
}

// Format wallet address for display
function formatAddress(address: string, length = 6): string {
  if (!address) return 'Unknown';
  if (address.length <= length * 2) return address;
  return `${address.slice(0, length)}...${address.slice(-4)}`;
}

// Format XRP amount from drops
function formatXRP(drops: string): string {
  const xrp = Number(drops) / 1_000_000;
  if (xrp === 0) return '';
  return `${xrp.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 })} XRP`;
}

interface NFTThumbnailProps {
  nft: TradeSummaryNFT;
}

function NFTThumbnail({ nft }: NFTThumbnailProps) {
  return (
    <Link
      to={`/nfts/${nft.token_id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-16 h-16 rounded-lg overflow-hidden bg-neutral-700 flex-shrink-0 ring-1 ring-neutral-border hover:ring-2 hover:ring-brand-primary transition-all"
      title={nft.name || nft.token_id}
    >
      {is3DModelUrl(nft.thumbnail_url) ? (
        <div className="w-full h-full flex items-center justify-center bg-neutral-100">
          <span style={{ fontSize: '20px' }}>🧊</span>
        </div>
      ) : nft.thumbnail_url ? (
        <img
          src={nft.thumbnail_url}
          alt={nft.name || 'NFT'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-subtext-color text-xs">
          NFT
        </div>
      )}
    </Link>
  );
}

interface TradeSidePreviewProps {
  label: string;
  nfts: TradeSummaryNFT[];
  xrpDrops: string;
  maxVisible?: number;
}

function TradeSidePreview({ label, nfts, xrpDrops, maxVisible = 4 }: TradeSidePreviewProps) {
  const visibleNfts = nfts.slice(0, maxVisible);
  const hiddenCount = nfts.length - maxVisible;
  const xrpAmount = formatXRP(xrpDrops);
  const hasContent = nfts.length > 0 || xrpAmount;

  if (!hasContent) {
    return (
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-subtext-color uppercase tracking-wide mb-3">{label}</p>
        <p className="text-sm text-subtext-color italic">Nothing</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-subtext-color uppercase tracking-wide mb-3">{label}</p>
      <div className="flex flex-wrap gap-2">
        {visibleNfts.map((nft) => (
          <NFTThumbnail key={nft.token_id} nft={nft} />
        ))}
        {hiddenCount > 0 && (
          <div className="w-16 h-16 rounded-lg bg-neutral-700 flex items-center justify-center text-sm font-medium text-default-font ring-1 ring-neutral-border">
            +{hiddenCount}
          </div>
        )}
      </div>
      {xrpAmount && (
        <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20">
          <span className="text-sm font-semibold text-amber-400">+ {xrpAmount}</span>
        </div>
      )}
    </div>
  );
}

interface TradeActivityCardProps {
  tradeId: string;
  tradeVersion?: number | null; // Version at time of message
  tradeAction: TradeAction;
  senderAddress: string;
  currentUserAddress: string;
  senderDisplayName?: string | null;
  content?: string; // Optional user message
  createdAt: string;
}

export function TradeActivityCard({
  tradeId,
  tradeVersion,
  tradeAction,
  senderAddress,
  currentUserAddress,
  senderDisplayName,
  content,
  createdAt,
}: TradeActivityCardProps) {
  // Fetch trade summary (with version for historical accuracy)
  const {
    data: summary,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['trade-summary', tradeId, tradeVersion],
    queryFn: () => getTradeSummary(tradeId, tradeVersion ?? undefined),
    staleTime: 60 * 1000, // 1 minute cache
    retry: 1,
  });

  const actionConfig = TRADE_ACTION_CONFIG[tradeAction] || { label: 'updated the trade', icon: '📋' };
  const isOwnAction = senderAddress === currentUserAddress;
  const senderName = isOwnAction
    ? 'You'
    : senderDisplayName || formatAddress(senderAddress);

  // Celebratory styling for completed trades
  const isCelebratory = actionConfig.isCelebratory;

  // Determine which tab to link to
  const tradesTab = getTradesTab(tradeAction);

  return (
    <div className="flex justify-center my-4">
      <div
        className={`w-full max-w-md rounded-xl overflow-hidden shadow-sm ${
          isCelebratory
            ? 'bg-default-background border border-green-500/40'
            : 'bg-default-background border border-neutral-border'
        }`}
      >
        {/* Header */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="text-lg">{actionConfig.icon}</span>
            <span className="text-sm text-default-font">
              {actionConfig.isSystemEvent ? (
                <span className="font-semibold">{actionConfig.label}</span>
              ) : (
                <>
                  <span className="font-semibold">{senderName}</span>{' '}
                  {isCelebratory ? '' : actionConfig.label}
                  {isCelebratory && (
                    <span className="text-green-400 font-semibold">
                      {actionConfig.label}
                    </span>
                  )}
                </>
              )}
            </span>
          </div>

          {/* Prompt to sign when the other party accepted */}
          {tradeAction === 'trade_accepted' && !isOwnAction && (
            <p className="mt-1.5 ml-8 text-xs text-amber-400 font-medium">
              Sign your side to complete the trade
            </p>
          )}
        </div>

        {/* Trade summary content */}
        <div className="px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-border border-t-subtext-color"></div>
            </div>
          ) : isError || !summary ? (
            <div className="text-center py-6">
              <p className="text-sm text-subtext-color">
                Trade no longer available
              </p>
            </div>
          ) : (
            <>
              {/* Two-column grid for give/receive */}
              <div className="flex gap-5">
                <TradeSidePreview
                  label="You give"
                  nfts={summary.viewer_gives.nfts}
                  xrpDrops={summary.viewer_gives.xrp_drops}
                />
                <div className="w-px bg-neutral-border self-stretch"></div>
                <TradeSidePreview
                  label="You receive"
                  nfts={summary.viewer_receives.nfts}
                  xrpDrops={summary.viewer_receives.xrp_drops}
                />
              </div>

              {/* Multi-collection warning */}
              <MultiCollectionWarning
                sideA={summary.viewer_gives.nfts}
                sideB={summary.viewer_receives.nfts}
                compact
                className="mt-3"
              />

              {/* Optional user message or system reason */}
              {content && content.trim() && (
                <div className="mt-4 pt-3 border-t border-neutral-border">
                  <p className="text-sm text-subtext-color italic leading-relaxed">
                    {actionConfig.isSystemEvent ? content : `"${content}"`}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer with link and timestamp */}
        <div className="px-4 py-2.5 flex items-center justify-between border-t border-neutral-border">
          <Link
            to={`/trades?tab=${tradesTab}`}
            className={`text-sm font-medium hover:underline transition-colors ${
              isCelebratory ? 'text-green-400 hover:text-green-300' : 'text-brand-primary hover:text-brand-primary/80'
            }`}
          >
            View trade →
          </Link>
          <span className="text-xs text-subtext-color">
            {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
          </span>
        </div>
      </div>
    </div>
  );
}
